import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import TextField from '@mui/material/TextField';
import { styled } from '@mui/material/styles';
import React from 'react';

const StyledTextarea = styled(TextField)(({ theme }) => ({
  '& .MuiInputBase-root': {
    fontWeight: 400,
    fontSize: '15px',
    color: theme.palette.inputField.font,
    lineHeight: '21px',
    background: theme.palette.inputField.background,
    borderRadius: theme.borderRadius.md,
    minHeight: 124,
    alignItems: 'flex-start',
    padding: 0,
    boxSizing: 'border-box',
    letterSpacing: '0px',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.inputField.border,
    borderWidth: 1,
  },
  '& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.inputField.hoverBorder,
  },
  '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.inputField.focusBorder,
  },
  '& .Mui-disabled .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.inputField.border,
  },
  '& .Mui-error .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.inputField.errorBorder,
  },
  '& textarea': {
    fontWeight: 400,
    fontSize: '15px',
    color: theme.palette.inputField.font,
    lineHeight: '21px',
    boxSizing: 'border-box',
    resize: 'none',
    padding: '17px 16px',
    background: 'transparent',
  },
  '& textarea::placeholder': {
    color: theme.palette.inputField.placeholder,
    opacity: 1,
  },
}));

interface TextAreaProps {
  label?: string;
  value: string;
  onChange?: (_event: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  minRows?: number;
  sx?: object;
  [key: string]: any;
}

const TextArea: React.FC<TextAreaProps> = React.memo(function TextArea({
  label,
  value,
  onChange,
  placeholder = 'Type here...',
  disabled = false,
  error = false,
  helperText,
  minRows = 2,
  maxRows = 4,
  sx = {},
  ...props
}) {
  return (
    <Box sx={{ width: '100%', ...sx }}>
      <FormControl variant="outlined" size="medium" fullWidth disabled={disabled} error={error}>
        {label && (
          <InputLabel
            shrink={false}
            sx={(theme) => ({
              position: 'static',
              transform: 'none',
              marginBottom: '8px',
              fontWeight: 500,
              fontSize: '14px',
              color: theme.palette.formField.label,
              lineHeight: '20px',
            })}
          >
            {label}
          </InputLabel>
        )}
        <StyledTextarea
          variant="outlined"
          multiline
          minRows={minRows}
          maxRows={maxRows}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          disabled={disabled}
          error={error}
          InputLabelProps={{ shrink: false }}
          {...props}
        />
        {helperText && (
          <Box
            sx={(theme) => ({
              fontSize: '12px',
              color: error
                ? theme.palette.formField.helperError
                : theme.palette.formField.helperDefault,
              marginTop: '4px',
              marginLeft: '12px',
            })}
          >
            {helperText}
          </Box>
        )}
      </FormControl>
    </Box>
  );
});

export default TextArea;
